import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AdminShell from "../components/AdminShell";
import { api } from "../lib/api";
import {
  ShieldCheck, Loader2, CheckCircle2, X, Clock, RefreshCw, FileSearch,
} from "lucide-react";

const STATUS_TABS = [
  { key: "pending", label: "Pending review" },
  { key: "verified", label: "Verified" },
  { key: "all", label: "All" },
];

export default function AdminVerifiedReadiness() {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [approveOpen, setApproveOpen] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/admin/verified-readiness/queue?status=${tab}`);
        if (!cancelled) {
          setRows(data.items || []);
          setErr("");
        }
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Couldn't load queue");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tab]);

  // Precompute per-row meta line + paid date string so JSX stays cheap.
  const processedRows = useMemo(() => rows.map((r) => ({
    ...r,
    isProject: r.kind === "project",
    metaLine: r.kind === "project"
      ? [(r.project_intake?.project_category || "").replace(/_/g, " "),
         r.project_intake?.location].filter(Boolean).join(" · ")
      : [r.sector, r.country, r.stage].filter(Boolean).join(" · "),
    paidDate: r.verified_readiness_paid_at
      ? new Date(r.verified_readiness_paid_at).toLocaleDateString(undefined,
          { day: "numeric", month: "short", year: "numeric" })
      : "—",
  })), [rows]);

  const refresh = () => {
    setLoading(true);
    api.get(`/admin/verified-readiness/queue?status=${tab}`)
      .then(({ data }) => { setRows(data.items || []); setErr(""); })
      .catch((e) => setErr(e?.response?.data?.detail || "Couldn't load queue"))
      .finally(() => setLoading(false));
  };

  return (
    <AdminShell>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#F59E0B]">
            <ShieldCheck className="h-3.5 w-3.5"/> Admin · Verified review queue
          </p>
          <h1 className="mt-1 text-2xl font-extrabold text-neutral-100">
            Verified Capital Readiness Score
          </h1>
          <p className="mt-1 text-sm text-neutral-400">
            Founders who requested a human-expert review (included free with a paid Capital Readiness Report). Approve once you&rsquo;ve completed
            the assessment — the founder gets a &ldquo;Verified by Clinq&rdquo; badge on their report.
          </p>
        </div>
        <button
          onClick={refresh}
          data-testid="admin-verified-refresh"
          className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-2 text-xs font-semibold text-neutral-200 hover:bg-neutral-800"
        >
          <RefreshCw className="h-3.5 w-3.5"/> Refresh
        </button>
      </div>

      <div className="mb-4 inline-flex rounded-lg border border-neutral-700 bg-neutral-900 p-1">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`admin-verified-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`rounded-md px-4 py-1.5 text-sm font-semibold transition ${
              tab === t.key ? "bg-[#F59E0B] text-neutral-950" : "text-neutral-400 hover:text-neutral-100"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading && (
        <div data-testid="admin-verified-loading"
             className="flex items-center justify-center rounded-2xl border border-neutral-800 bg-neutral-900 p-12 text-sm text-neutral-400">
          <Loader2 className="mr-2 h-4 w-4 animate-spin"/> Loading…
        </div>
      )}

      {!loading && err && (
        <p className="rounded-lg border border-red-900 bg-red-950/40 p-4 text-sm text-red-300">{err}</p>
      )}

      {!loading && !err && rows.length === 0 && (
        <div data-testid="admin-verified-empty"
             className="rounded-2xl border border-neutral-800 bg-neutral-900 p-12 text-center">
          <Clock className="mx-auto h-8 w-8 text-neutral-600"/>
          <p className="mt-3 text-sm text-neutral-400">
            {tab === "pending" ? "No pending verified reviews." : "No verified reports yet."}
          </p>
        </div>
      )}

      {!loading && !err && rows.length > 0 && (
        <div data-testid="admin-verified-table"
             className="overflow-hidden rounded-2xl border border-neutral-800 bg-neutral-900">
          <table className="min-w-full text-sm">
            <thead className="bg-neutral-950 text-[11px] font-bold uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="px-4 py-3 text-left">Business</th>
                <th className="px-4 py-3 text-left">Founder</th>
                <th className="px-4 py-3 text-right">Indicative</th>
                <th className="px-4 py-3 text-right">Verified</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Paid</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800">
              {processedRows.map((r) => (
                <tr key={r.company_id} data-testid={`admin-verified-row-${r.company_id}`}
                    className="hover:bg-neutral-800/40">
                  <td className="px-4 py-3">
                    <Link to={`/admin/businesses/${r.company_id}`}
                          className="font-bold text-neutral-100 hover:text-[#F59E0B]">
                      {r.company_name || "(unnamed)"}
                    </Link>
                    {r.isProject && (
                      <span data-testid={`admin-verified-project-badge-${r.company_id}`}
                            className="ml-2 rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-400">
                        Project
                      </span>
                    )}
                    <p className="text-[11px] text-neutral-500">{r.metaLine}</p>
                  </td>
                  <td className="px-4 py-3 text-neutral-300">
                    {r.owner_email || "—"}
                    {r.owner_name && <p className="text-[11px] text-neutral-500">{r.owner_name}</p>}
                  </td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-neutral-300">
                    {r.readiness_score ?? "—"}
                  </td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-emerald-400">
                    {r.verified_readiness_score ?? "—"}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={r.verified_readiness_status}/>
                  </td>
                  <td className="px-4 py-3 text-neutral-400 text-xs">{r.paidDate}</td>
                  <td className="px-4 py-3 text-right">
                    {r.verified_readiness_status === "pending" ? (
                      <button
                        data-testid={`admin-verified-approve-btn-${r.company_id}`}
                        onClick={() => setApproveOpen(r)}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-500"
                      >
                        <CheckCircle2 className="h-3.5 w-3.5"/> Approve & verify
                      </button>
                    ) : (
                      <Link to={`/admin/businesses/${r.company_id}`}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-1.5 text-xs font-semibold text-neutral-200 hover:bg-neutral-800">
                        <FileSearch className="h-3.5 w-3.5"/> Open
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {approveOpen && (
        <ApproveModal
          row={approveOpen}
          onClose={() => setApproveOpen(null)}
          onDone={() => { setApproveOpen(null); refresh(); }}
        />
      )}
    </AdminShell>
  );
}

function StatusBadge({ status }) {
  if (status === "verified") {
    return <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-[11px] font-bold text-emerald-300">
      <ShieldCheck className="h-3 w-3"/> Verified
    </span>;
  }
  return <span className="inline-flex items-center gap-1 rounded-full bg-[#F59E0B]/15 px-2.5 py-0.5 text-[11px] font-bold text-[#F59E0B]">
    <Clock className="h-3 w-3"/> Pending
  </span>;
}

function ApproveModal({ row, onClose, onDone }) {
  const [score, setScore] = useState(row.verified_readiness_score ?? row.readiness_score ?? 0);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post(`/admin/verified-readiness/${row.company_id}/approve`,
        { verified_score: Number(score), notes });
      onDone();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Couldn't approve");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-verified-approve-modal"
         className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <form onSubmit={submit}
            className="w-full max-w-md rounded-2xl border border-neutral-800 bg-neutral-950 p-6 shadow-2xl">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#F59E0B]">
              Approve verified review
            </p>
            <h3 className="mt-1 text-lg font-bold text-neutral-100">{row.company_name}</h3>
            <p className="text-[11px] text-neutral-500">
              Indicative score was {row.readiness_score ?? "—"} / 100
            </p>
          </div>
          <button type="button" onClick={onClose}
                  className="rounded-md p-1 text-neutral-500 hover:bg-neutral-800">
            <X className="h-5 w-5"/>
          </button>
        </div>

        <label className="mt-5 block text-xs font-semibold uppercase tracking-wider text-neutral-400">
          Verified score (0–100)
        </label>
        <input
          data-testid="admin-verified-score-input"
          type="number"
          min={0}
          max={100}
          required
          value={score}
          onChange={(e) => setScore(e.target.value)}
          className="mt-2 w-full rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-2 text-3xl font-extrabold text-emerald-400 focus:border-[#F59E0B] focus:outline-none"
        />

        <label className="mt-4 block text-xs font-semibold uppercase tracking-wider text-neutral-400">
          Reviewer notes <span className="text-neutral-500 normal-case">(visible to the founder)</span>
        </label>
        <textarea
          data-testid="admin-verified-notes-input"
          rows={5}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={1500}
          placeholder="Summarise what you reviewed, key strengths/risks, and any next steps the founder should focus on."
          className="mt-2 w-full rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-2 text-sm text-neutral-200 focus:border-[#F59E0B] focus:outline-none"
        />

        {err && <p className="mt-3 text-sm text-red-400">{err}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy}
                  className="rounded-lg bg-neutral-800 px-4 py-2 text-sm font-semibold text-neutral-200 hover:bg-neutral-700 disabled:opacity-50">
            Cancel
          </button>
          <button
            data-testid="admin-verified-approve-submit"
            type="submit"
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2 text-sm font-bold text-white hover:bg-emerald-500 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin"/> : <ShieldCheck className="h-4 w-4"/>}
            {busy ? "Approving…" : "Approve & verify"}
          </button>
        </div>
      </form>
    </div>
  );
}
